/**
 * Colonna sinistra del client email: elenco cartelle dell'account con
 * contatore non letti + creazione/eliminazione cartelle custom
 * (bidirezionale su IMAP).
 */

import React, { useState } from 'react';
import {
  AlertOctagon,
  Archive,
  FileText,
  Folder,
  FolderPlus,
  Inbox,
  Send,
  Trash2,
  X,
} from 'lucide-react';
import { Button, Input, Tooltip } from '@floraltech/ui';
import type { EmailCartella, EmailEtichetta } from '../lib/api';

const RUOLO_ICONE: Record<string, React.ComponentType<{ className?: string }>> = {
  inbox: Inbox,
  sent: Send,
  trash: Trash2,
  drafts: FileText,
  archive: Archive,
  junk: AlertOctagon,
};

const RUOLO_ORDINE: Record<string, number> = {
  inbox: 0,
  sent: 1,
  drafts: 2,
  archive: 3,
  junk: 4,
  trash: 5,
  custom: 6,
};

interface Props {
  cartelle: EmailCartella[];
  selezionataId: string | null;
  onSeleziona: (cartella: EmailCartella) => void;
  onCrea: (nome: string) => Promise<boolean>;
  onEliminaCartella: (cartella: EmailCartella) => void;
  onDropMessaggi: (cartella: EmailCartella, ids: string[]) => void;
  /** Casella PEC senza email.pec_gestione: niente crea/elimina cartella, drop dnd disattivo. */
  soloLettura?: boolean;
  /** Etichette (tag) del tenant, sezione sotto l'albero cartelle. Filtro trasversale alle cartelle. */
  etichette: EmailEtichetta[];
  etichettaSelezionataId: string | null;
  onSelezionaEtichetta: (id: string) => void;
}

export function CartelleSidebar({
  cartelle,
  selezionataId,
  onSeleziona,
  onCrea,
  onEliminaCartella,
  onDropMessaggi,
  soloLettura = false,
  etichette,
  etichettaSelezionataId,
  onSelezionaEtichetta,
}: Props) {
  const [creazione, setCreazione] = useState(false);
  const [nome, setNome] = useState('');
  const [salvataggio, setSalvataggio] = useState(false);
  const [dropTargetId, setDropTargetId] = useState<string | null>(null);

  const ordinate = [...cartelle].sort(
    (a, b) =>
      (RUOLO_ORDINE[a.ruolo ?? 'custom'] ?? 6) - (RUOLO_ORDINE[b.ruolo ?? 'custom'] ?? 6) ||
      (a.nome_display ?? a.nome_imap).localeCompare(b.nome_display ?? b.nome_imap)
  );

  const crea = async () => {
    if (!nome.trim()) return;
    setSalvataggio(true);
    try {
      if (await onCrea(nome.trim())) {
        setNome('');
        setCreazione(false);
      }
    } finally {
      setSalvataggio(false);
    }
  };

  return (
    <nav className="flex flex-col gap-0.5 p-2">
      {ordinate.map((c) => {
        const Icona = RUOLO_ICONE[c.ruolo ?? ''] ?? Folder;
        const attiva = c.id === selezionataId;
        return (
          <div
            key={c.id}
            className={`group flex items-center rounded-md ${
              dropTargetId === c.id ? 'ring-2 ring-primary' : ''
            }`}
            onDragOver={(e) => {
              if (
                !soloLettura &&
                c.id !== selezionataId &&
                e.dataTransfer.types.includes('application/x-email-ids')
              ) {
                e.preventDefault();
                e.dataTransfer.dropEffect = 'move';
                setDropTargetId(c.id);
              }
            }}
            onDragLeave={() => setDropTargetId((id) => (id === c.id ? null : id))}
            onDrop={(e) => {
              setDropTargetId(null);
              if (soloLettura || c.id === selezionataId) return;
              const raw = e.dataTransfer.getData('application/x-email-ids');
              if (!raw) return;
              e.preventDefault();
              try {
                const ids: string[] = JSON.parse(raw);
                if (Array.isArray(ids) && ids.length > 0) onDropMessaggi(c, ids);
              } catch {
                /* payload non nostro: ignora */
              }
            }}
          >
            <button
              onClick={() => onSeleziona(c)}
              className={`flex flex-1 items-center gap-2 rounded-md px-3 py-2 text-sm text-left transition-colors ${
                attiva ? 'bg-primary/10 text-primary font-medium' : 'hover:bg-muted'
              }`}
            >
              <Icona className="h-4 w-4 shrink-0" />
              <span className="flex-1 truncate">{c.nome_display ?? c.nome_imap}</span>
              {c.num_non_letti > 0 && (
                <span className="rounded-full bg-primary px-2 py-0.5 text-xs text-primary-foreground">
                  {c.num_non_letti}
                </span>
              )}
            </button>
            {!soloLettura && (c.ruolo === 'custom' || c.ruolo == null) && (
              <Tooltip label={`Elimina cartella ${c.nome_display ?? c.nome_imap}`}>
                <button
                  type="button"
                  className="hidden p-1 text-muted-foreground hover:text-destructive group-hover:block"
                  aria-label={`Elimina cartella ${c.nome_display ?? c.nome_imap}`}
                  onClick={() => onEliminaCartella(c)}
                >
                  <X className="h-3 w-3" />
                </button>
              </Tooltip>
            )}
          </div>
        );
      })}
      {ordinate.length === 0 && (
        <p className="px-3 py-2 text-sm text-muted-foreground">
          Nessuna cartella sincronizzata.
        </p>
      )}

      {!soloLettura && (
        <div className="mt-2 border-t pt-2">
          {creazione ? (
            <div className="flex items-center gap-1">
              <Input
                value={nome}
                autoFocus
                placeholder="Nome cartella"
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setNome(e.target.value)}
                onKeyDown={(e: React.KeyboardEvent) => {
                  if (e.key === 'Enter') crea();
                  if (e.key === 'Escape') setCreazione(false);
                }}
              />
              <Button size="sm" onClick={crea} disabled={salvataggio || !nome.trim()}>
                Ok
              </Button>
            </div>
          ) : (
            <button
              type="button"
              className="flex w-full items-center gap-2 rounded-md px-3 py-2 text-sm text-muted-foreground hover:bg-muted"
              onClick={() => setCreazione(true)}
            >
              <FolderPlus className="h-4 w-4" />
              Nuova cartella
            </button>
          )}
        </div>
      )}

      {etichette.filter((e) => e.attiva).length > 0 && (
        <div className="mt-2 border-t pt-2">
          <p className="px-3 pb-1 text-xs font-medium uppercase text-muted-foreground">Tag</p>
          {etichette
            .filter((e) => e.attiva)
            .sort((a, b) => a.ordine - b.ordine || a.nome.localeCompare(b.nome))
            .map((e) => {
              const attiva = e.id === etichettaSelezionataId;
              return (
                <button
                  key={e.id}
                  type="button"
                  onClick={() => onSelezionaEtichetta(e.id)}
                  className={`flex w-full items-center gap-2 rounded-md px-3 py-2 text-sm text-left transition-colors ${
                    attiva ? 'bg-primary/10 text-primary font-medium' : 'hover:bg-muted'
                  }`}
                >
                  <span
                    className="h-2.5 w-2.5 shrink-0 rounded-full"
                    style={{ backgroundColor: e.colore }}
                    aria-hidden="true"
                  />
                  <span className="flex-1 truncate">{e.nome}</span>
                </button>
              );
            })}
        </div>
      )}
    </nav>
  );
}
